/**
 * Sprint 22 — DestructiveOpConfirmation.
 *
 * A reusable confirm-before-destroy modal (built on the ConfirmModal primitive)
 * for destructive actions the chat surface owns. v1 wires it to chat-session
 * delete/archive from the history sidebar.
 *
 * NOTE (scope honesty): the spec's "intercept the AI's destructive *tool calls*
 * before they execute" needs human-in-the-loop tool approval. The worker runs
 * tools autonomously inside streamText, and the installed assistant-ui runtime
 * has no approval surface (and this @ai-sdk/react has no `sendAutomaticallyWhen`),
 * so intercepting mid-stream would risk hanging the chat. v1 therefore relies on
 * server-side owner-gating (every `delete_*` is OWNER_ONLY → friendly inline
 * "requires owner" error) plus soft-archive recoverability, with full pre-execute
 * approval (AI SDK v6 `needsApproval` + `addToolApprovalResponse`) as the v1.1
 * follow-up. This component is the UI for that follow-up, and is live today for
 * session delete/archive. See [[design-admin-chat-cockpit]].
 */

import { ConfirmModal } from "../primitives";

export interface PendingDestructiveOp {
  /** The tool / action name, e.g. "delete_chat_session". */
  toolName: string;
  /** Human one-liner, e.g. 'Delete chat session "Q3 planning"'. */
  summary: string;
  /** Optional args/detail preview shown under the summary. */
  detail?: string;
}

interface DestructiveOpConfirmationProps {
  op: PendingDestructiveOp | null;
  onConfirm: () => void | Promise<void>;
  onCancel: () => void;
}

function confirmLabelFor(toolName: string): string {
  if (toolName.startsWith("delete")) return "Delete";
  if (toolName.startsWith("archive")) return "Archive";
  if (toolName.startsWith("bulk")) return "Apply";
  return "Confirm";
}

export function DestructiveOpConfirmation({ op, onConfirm, onCancel }: DestructiveOpConfirmationProps) {
  return (
    <ConfirmModal
      open={op !== null}
      onOpenChange={(open) => {
        if (!open) onCancel();
      }}
      title="Confirm action"
      message={
        op ? (
          <span>
            {op.summary}
            {op.detail ? (
              <code
                style={{
                  display: "block",
                  marginTop: 8,
                  padding: "6px 8px",
                  background: "var(--color-background-secondary)",
                  borderRadius: "var(--border-radius-sm)",
                  fontFamily: "var(--font-mono)",
                  fontSize: 12,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {op.detail}
              </code>
            ) : null}
          </span>
        ) : (
          ""
        )
      }
      confirmLabel={op ? confirmLabelFor(op.toolName) : "Confirm"}
      variant="danger"
      onConfirm={onConfirm}
    />
  );
}
